import { IcoUpload } from "assets/icons";
import { Button, Loading, SelectionFooter } from "components";
import { DownloadHelper, ErrorHelper } from "helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import { toast } from "react-toastify";
import { ApplicationDocumentService } from "services/applicationDocument";
import { ApplicationService } from "services/applicationService";
import { ApplicationDocumentFolder, ZipDownloadRequest } from "services/types";
import { DownloadWarningModal } from "../../components";
import { TabComponentProps } from "../../types";
import { FolderList } from "./components";

export default function ApplicationPageDocuments(props: Readonly<TabComponentProps>): JSX.Element {
	const { application } = props;
	const applicationId = application?.id;
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const applicationDocumentService = useMemo(ApplicationDocumentService.getInstance, []);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [applicationDocumentFolders, setApplicationDocumentFolders] = useState<ApplicationDocumentFolder[]>([]);
	const [applicationDocumentFolderVersions, setApplicationDocumentFolderVersions] = useState<{
		[key: string]: number;
	}>({});
	const [isZipDownloadWarningModalOpen, setIsZipDownloadWarningModalOpen] = useState(false);
	const [foldersSelected, setFoldersSelected] = useState<{
		[key: string]: boolean;
	}>({});
	const [isLoadingDownloadZip, setIsLoadingDownloadZip] = useState(false);

	const updateFoldersSelected = (prevState: Record<string, boolean>, folderId: string, isChecked: boolean) => ({
		...prevState,
		[folderId]: isChecked
	});
	const handleSelectFolders = useCallback(
		(isChecked: boolean, value: ApplicationDocumentFolder) => {
			setFoldersSelected((prevFolder) => updateFoldersSelected(prevFolder, value.exemptionDocumentFolderType.documentType.id, isChecked));
			updateFoldersSelected(foldersSelected, value.exemptionDocumentFolderType.documentType.id, isChecked);
		},
		[applicationDocumentFolders, setFoldersSelected, foldersSelected]
	);

	const loadInfo = useCallback(() => {
		if (!applicationId) return;
		setIsLoading(true);
		applicationService
			.listApplicationFolders(applicationId)
			.then((responseData) => {
				setApplicationDocumentFolders(responseData);
				setApplicationDocumentFolderVersions(
					responseData.reduce(
						(acc, folder) => ({
							...acc,
							[folder.exemptionDocumentFolderType.id]: folder.version
						}),
						{}
					)
				);
			})
			.catch((err) => toast.error(ErrorHelper.getResponseErrorMessage(err)))
			.finally(() => setIsLoading(false));
	}, [applicationId]);

	useEffect(() => {
		loadInfo();
	}, [applicationId]);

	const replaceDocumentFolderInList = useCallback((exemptionDocumentFolderTypeId: string, newFolder: ApplicationDocumentFolder) => {
		setApplicationDocumentFolders((folders) =>
			folders.map((folder) => (folder.exemptionDocumentFolderType.id === exemptionDocumentFolderTypeId ? newFolder : folder))
		);
	}, []);
	const blocker = useBlocker(({ currentLocation, nextLocation }) => isLoadingDownloadZip && currentLocation.pathname !== nextLocation.pathname);

	useBeforeUnload((event) => {
		if (isLoadingDownloadZip) {
			event.preventDefault();
			return "Processamento dos arquivos em andamento. Tem certeza que deseja cancelar?";
		}
		return undefined;
	});

	useEffect(() => {
		if (blocker.state === "blocked") {
			setIsZipDownloadWarningModalOpen(true);
		} else {
			setIsZipDownloadWarningModalOpen(false);
		}
	}, [blocker]);

	const selectedFolders = () => {
		const filterSelectedFolders = applicationDocumentFolders.filter(
			(folder) => foldersSelected[folder.exemptionDocumentFolderType.documentType.id]
		);

		return {
			containerName: "documents",
			applicationProcessCode: `${application?.applicationProcessCode}`,
			downloadedFolders: filterSelectedFolders.map((folder) => ({
				folderName: folder.exemptionDocumentFolderType.documentType.name,
				fileNames: folder.documents.map((document) => document.blobName)
			}))
		};
	};

	const zipDownload = useCallback(
		async (zipDownloadRequest: ZipDownloadRequest) => {
			if (!applicationDocumentService) {
				return;
			}
			setIsLoadingDownloadZip(true);

			applicationDocumentService
				.zipDownload(zipDownloadRequest)
				.then((response) => {
					toast.success(
						<div>
							<div className="text-base font-semibold">Download realizado!</div>
							<div className="text-sm/6">O download dos arquivos foram feitos com sucesso.</div>
						</div>
					);
					const { downloadUrls } = response;
					downloadUrls.forEach((url, i) => DownloadHelper.performDownload(url, `Parte ${i}`));
				})
				.catch((error) => toast.error(ErrorHelper.getResponseErrorMessage(error)))
				.finally(() => {
					setIsLoadingDownloadZip(false);
					blocker.reset?.();
					setIsZipDownloadWarningModalOpen(false);
				});
		},
		[applicationDocumentService, applicationDocumentFolders, foldersSelected]
	);

	const selectedFoldersNumber = Object.values(foldersSelected).filter(Boolean).length;

	const selectionLabel = () => {
		const numberRows = selectedFoldersNumber;
		const labelName = numberRows > 1 ? "Pastas" : "Pasta";
		const labelSelected = numberRows > 1 ? "selecionadas" : "selecionada";
		return (
			<>
				<b>{`${numberRows}`}</b> <span>{`${labelName} `}</span>
				{labelSelected}
			</>
		);
	};

	return isLoading ? (
		<div className="bg-neutral-high-pure-50 rounded-lg flex flex-col justify-start grow overflow-hidden max-h-[70vh]">
			<div className="h-full content-center">
				<Loading size={50} />
			</div>
		</div>
	) : (
		<div className="bg-neutral-high-pure-50 rounded-lg flex py-6 flex-col justify-start overflow-hidden overflow-y-auto grow max-h-[70vh]">
			<div className="w-full px-6 overflow-wrap--anywhere scrollbar-width--thin scrollbar-gutter--stable">
				<FolderList
					selectedFolders={foldersSelected}
					handleSelectFolders={handleSelectFolders}
					id="folder-list"
					folders={applicationDocumentFolders}
					applicationDocumentService={applicationDocumentService}
					foldersCurrentVersions={applicationDocumentFolderVersions}
					applicationId={applicationId!}
					replaceDocumentFolderInList={replaceDocumentFolderInList}
				/>
				<SelectionFooter open={selectedFoldersNumber > 0} label={selectionLabel()}>
					<Button className="first-icon" onClick={() => zipDownload(selectedFolders())} isLoading={isLoadingDownloadZip}>
						<IcoUpload color="#FFF" size="16" />
						<span>Exportar</span>
					</Button>
				</SelectionFooter>
			</div>

			<DownloadWarningModal isOpen={isZipDownloadWarningModalOpen} onClose={() => blocker.reset?.()} />
		</div>
	);
}
